import * as THREE from "three";
import { buildBoard, BOARD_OUTER_HALF_SPAN, CAPTURE_RAIL_Z, TABLE_TOP_Y, type BoardMeshes } from "./board";
import { TweenManager } from "./animation";
import { loadRealWoodTexture } from "./textures";
import { placeCandles, type Candle } from "./candles";
import type { Color } from "../game/types";
import skyboxUrl from "../assets/env/tavern-skybox.jpg";

const RADIUS = 13.5;
const BASE_FOV = 38;
const ELEVATION_FROM_HORIZONTAL = THREE.MathUtils.degToRad(60);
const USER_AZIMUTH_MARGIN = THREE.MathUtils.degToRad(70);
const USER_ELEVATION_MARGIN = THREE.MathUtils.degToRad(24);
const PIVOT_DURATION = 1.1;
// Plafond applique au delta de la boucle de rendu : sans lui, une frame
// exceptionnellement lente (chargement de la skybox, premiere compilation de
// shaders, reconstruction de la shadow map juste au lancement de l'intro) fait
// avaler d'un coup plusieurs "secondes virtuelles" aux tweens en cours (camera,
// pieces qui tombent), qui se retrouvent alors termines en une poignee de
// frames au lieu de leur duree prevue. Std. game-loop safety net.
const MAX_FRAME_DELTA = 1 / 20;
// Point de depart de l'entree en scene "plongeante" (`playIntroSwoop`) : quasi
// a la verticale (mais pas exactement 90°, qui degenere le lookAt) et plus
// recule, pour plonger vers la position de jeu normale plutot que d'y apparaitre
// directement.
const INTRO_START_ELEVATION = THREE.MathUtils.degToRad(85);
const INTRO_START_RADIUS_SCALE = 1.4;
// Plus long que le fondu du rideau (700ms, cf. `SceneFade.fromBlack`) et en
// easing par defaut (accelere puis decelere) : le rideau devient transparent
// bien avant la fin de la plongee, pour que le mouvement de camera reste
// visible une fois le plateau revele, pas termine en coulisses derriere le noir.
const INTRO_SWOOP_DURATION = 1.6;
const TABLE_RADIUS = 12;
const TABLE_HEIGHT = 1.2;
// Marge horizontale (au-dela du bord du cadre) gardee visible de chaque cote du
// plateau, pour que les cases de bord restent confortables a toucher sur mobile.
const BOARD_FIT_MARGIN = 1.08;
// Filet de securite sur les ratios d'ecran extremes (fenetre tres etroite, pli de
// telephone...) : au-dela, on arrete d'elargir le champ de vision plutot que de
// verser dans une distorsion fisheye ingerable.
const MAX_FOV = 100;

/**
 * Trois niveaux de rendu, du plus riche au plus econome — meme ambiance (bois,
 * chandelles, brouillard chaud), juste moins de pixels/ombres/lumiere a calculer
 * en continu. Reglable a tout moment (menu ou en cours de partie, cf. `setQuality`) :
 * l'antialiasing reste donc fixe (il ne peut changer qu'a la creation du contexte
 * WebGL), seuls la resolution, les ombres, le vacillement des chandelles et le
 * plafond de cadence varient d'un niveau a l'autre.
 */
export type GraphicsQuality = "high" | "standard" | "battery";

interface QualityConfig {
  readonly pixelRatioCap: number;
  readonly shadowsEnabled: boolean;
  readonly shadowMapSize: number;
  /** Cadence de rendu plafonnee (images/s) ; `null` = pas de plafond (rendu a chaque `requestAnimationFrame`). */
  readonly targetFps: number | null;
  /** Chandelles qui vacillent (mise a jour de la lumiere/flamme chaque frame) ou flamme fixe (calculee une fois). */
  readonly animateCandles: boolean;
}

const QUALITY_CONFIGS: Record<GraphicsQuality, QualityConfig> = {
  high: { pixelRatioCap: 2, shadowsEnabled: true, shadowMapSize: 1024, targetFps: null, animateCandles: true },
  standard: { pixelRatioCap: 1.5, shadowsEnabled: true, shadowMapSize: 768, targetFps: null, animateCandles: true },
  battery: { pixelRatioCap: 1, shadowsEnabled: false, shadowMapSize: 512, targetFps: 30, animateCandles: false },
};

/** Intensite/echelle de repos des chandelles (milieu de leur plage de vacillement),
 * utilisee comme etat fige quand le vacillement est desactive (mode "battery"). */
const CANDLE_REST_INTENSITY = 4.6;
const CANDLE_REST_SCALE = new THREE.Vector3(0.65, 1.4, 0.65);

/** Scene 3D complete : rendu, camera en plongee face-a-face (pivote au changement de tour,
 * avec marge de rotation libre a la souris/tactile), eclairage chaleureux, plateau en bois. */
export class SceneManager {
  readonly scene = new THREE.Scene();
  readonly camera: THREE.PerspectiveCamera;
  readonly renderer: THREE.WebGLRenderer;
  readonly tweens = new TweenManager();
  readonly board: BoardMeshes;
  readonly piecesGroup = new THREE.Group();

  private container: HTMLElement;
  private baseAzimuth = 0;
  private userAzimuthOffset = 0;
  private userElevationOffset = 0;
  // Ecart temporaire par rapport a l'elevation/au rayon normaux, utilise uniquement
  // pendant `playIntroSwoop` ; nul (donc sans effet sur `updateCameraPosition`) le
  // reste du temps.
  private introElevationBoost = 0;
  private introRadiusMultiplier = 1;
  private clock = new THREE.Clock();
  private dragging = false;
  private lastPointer = { x: 0, y: 0 };
  private raycaster = new THREE.Raycaster();
  private candles: Candle[] = [];
  private disposed = false;
  private quality: QualityConfig;
  private frameAccumulator = 0;
  private keyLight!: THREE.DirectionalLight;

  constructor(container: HTMLElement, quality: GraphicsQuality = "standard") {
    this.container = container;
    this.quality = QUALITY_CONFIGS[quality];

    this.renderer = new THREE.WebGLRenderer({ antialias: true });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, this.quality.pixelRatioCap));
    this.renderer.shadowMap.enabled = this.quality.shadowsEnabled;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    // Ombres recalculees seulement quand quelque chose bouge (cf. `loop`), pas a
    // chaque frame indefiniment : le plateau est statique la plupart du temps
    // (l'IA/l'adversaire reflechit), inutile de refaire la passe d'ombres en boucle.
    this.renderer.shadowMap.autoUpdate = false;
    this.renderer.shadowMap.needsUpdate = true;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.35;
    container.appendChild(this.renderer.domElement);

    this.camera = new THREE.PerspectiveCamera(BASE_FOV, 1, 0.1, 100);

    this.scene.background = new THREE.Color("#120a04");
    this.scene.fog = new THREE.Fog("#120a04", 14, 26);
    this.loadSkybox();

    this.setupFloor();
    this.setupLighting();
    this.setupTable();
    this.board = buildBoard();
    this.scene.add(this.board.group);
    this.scene.add(this.piecesGroup);

    this.updateCameraPosition();
    this.handleResize();
    window.addEventListener("resize", this.handleResize);

    this.renderer.domElement.addEventListener("pointerdown", this.onPointerDown);
    window.addEventListener("pointermove", this.onPointerMove);
    window.addEventListener("pointerup", this.onPointerUp);

    this.loop();
  }

  /** Panorama 360 de taverne (fourni par l'utilisateur) : remplace le decor
   * procedural (sol + mur cylindrique de environment.ts) comme arriere-plan
   * ET comme source de lumiere ambiante/reflets (scene.environment), via
   * PMREMGenerator — technique standard three.js pour un equirectangulaire
   * charge au runtime. Meme image que dkg-pegsolitaire (demande explicite de
   * reutilisation entre jeux du studio). */
  private loadSkybox(): void {
    const pmremGenerator = new THREE.PMREMGenerator(this.renderer);
    pmremGenerator.compileEquirectangularShader();
    new THREE.TextureLoader().load(skyboxUrl, (texture) => {
      texture.mapping = THREE.EquirectangularReflectionMapping;
      texture.colorSpace = THREE.SRGBColorSpace;
      // Anisotropie maximale : sans elle, l'echantillonnage en incidence
      // rasante (fond spherique vu de biais) floute nettement l'image.
      texture.anisotropy = this.renderer.capabilities.getMaxAnisotropy();
      // Fond = texture BRUTE (nette) ; le PMREM (prefiltre, flou par
      // construction pour l'eclairage/les reflets) ne sert qu'a
      // scene.environment, sinon la skybox affichee est floue.
      this.scene.background = texture;
      this.scene.environment = pmremGenerator.fromEquirectangular(texture).texture;
      pmremGenerator.dispose();
    });
  }

  /** Simple capteur d'ombres (invisible sauf la ou une ombre tombe dessus) :
   * la skybox fournit deja le sol visuellement. */
  private setupFloor(): void {
    const floor = new THREE.Mesh(new THREE.CircleGeometry(16, 32), new THREE.ShadowMaterial({ opacity: 0.35 }));
    floor.rotation.x = -Math.PI / 2;
    floor.position.y = -1.81;
    floor.receiveShadow = true;
    this.scene.add(floor);
  }

  private setupLighting(): void {
    // Composante "sol" eclaircie (#241a0e -> #33261a) : c'est elle qui plafonne le
    // niveau d'ombre le plus sombre de la scene (dessous des pieces, cote a l'oppose
    // de la lumiere cle) — remontee un peu pour que les pieces noires y gardent du
    // detail au lieu de virer au silhouette pure.
    const ambient = new THREE.HemisphereLight("#7a6a54", "#33261a", 1.4);
    this.scene.add(ambient);

    const key = new THREE.DirectionalLight("#ffd9a8", 1.25);
    key.position.set(4, 9, 3);
    key.castShadow = this.quality.shadowsEnabled;
    key.shadow.mapSize.set(this.quality.shadowMapSize, this.quality.shadowMapSize);
    key.shadow.camera.left = -6;
    key.shadow.camera.right = 6;
    key.shadow.camera.top = 6;
    key.shadow.camera.bottom = -6;
    this.scene.add(key);
    this.keyLight = key;

    // Contre-jour depuis le cote oppose, pour adoucir les ombres tres marquees
    // laissees nues par la seule lumiere cle (pas d'ombres portees : cout minime).
    // Intensite doublee : eclaire le cote du plateau oppose a la lumiere cle, donc
    // notamment les pieces de l'adversaire (loin de la camera, plus petites a
    // l'ecran, elles avaient besoin de plus de lumiere directe pour rester lisibles).
    const fill = new THREE.DirectionalLight("#ffb98a", 1.2);
    fill.position.set(-5, 5, -6);
    this.scene.add(fill);
    // Relevee au niveau de `fill` (0.9 -> 1.2) : le plateau pivote a chaque tour, ce
    // cote n'est donc pas systematiquement celui du joueur actif — les pieces noires
    // qui s'y trouvent avaient besoin d'autant de lumiere directe que sur l'autre cote.
    const fillFar = new THREE.DirectionalLight("#ffb98a", 1.2);
    fillFar.position.set(5, 5, 6);
    this.scene.add(fillFar);

    // Lumiere centrale, forte, bien au-dessus du milieu du plateau : eclaire
    // uniformement les deux cotes (le sien et celui de l'adversaire), plutot
    // qu'une simple lueur d'ambiance.
    const centerLight = new THREE.PointLight("#fff1d6", 5.5, 26, 1.4);
    centerLight.position.set(0, 6, 0);
    this.scene.add(centerLight);

    // Lumiere douce au-dessus du plateau, pour bien lire le relief des pieces en plongee.
    const overhead = new THREE.PointLight("#fff1d6", 2.4, 22, 1.5);
    overhead.position.set(0, 7, 0);
    this.scene.add(overhead);

    // Positions choisies pour rester dans le champ de la camera des deux cotes du pivot
    // (symetriques par rapport a l'origine : verifie en projetant a l'ecran pour les deux azimuts).
    const candlePositions: [number, number][] = [
      [-6.4, 0],
      [6.4, 0],
    ];
    this.candles = placeCandles(this.scene, candlePositions, TABLE_TOP_Y, 3.4);

    // Lumieres dediees aux rails de pieces capturees : loin du plateau (donc peu
    // atteintes par les lumieres ci-dessus, a decroissance rapide) et hors du champ
    // des chandelles (posees sur les cotes, pas devant/derriere), les pieces
    // capturees s'y fondaient dans le fond tres sombre — surtout les foncees.
    const railLightZ = CAPTURE_RAIL_Z + 0.5;
    const whiteRailLight = new THREE.PointLight("#ffcf9e", 3.6, 8, 1.6);
    whiteRailLight.position.set(0, 2.1, -railLightZ);
    this.scene.add(whiteRailLight);
    const blackRailLight = new THREE.PointLight("#ffcf9e", 3.6, 8, 1.6);
    blackRailLight.position.set(0, 2.1, railLightZ);
    this.scene.add(blackRailLight);
  }

  private setupTable(): void {
    // Un seul exemplaire etire (repeat=1), pas de carrelage : comme pour le dessus du
    // couvercle du coffret de dkg-farkle, une photo de bois tuilee sur une grande face
    // plate montre nettement le raccord de repetition ; en repeat=1 le grain (noeuds,
    // veines) se lit pleinement, sans couture, et se laisse enfin bien apprecier.
    const topTexture = loadRealWoodTexture(1, 1);
    const topMaterial = new THREE.MeshStandardMaterial({ map: topTexture, color: "#7a5230", roughness: 0.7 });
    const sideTexture = loadRealWoodTexture(8, 1);
    const sideMaterial = new THREE.MeshStandardMaterial({ map: sideTexture, color: "#2a1a0c", roughness: 0.85 });

    const tableGeometry = new THREE.CylinderGeometry(TABLE_RADIUS, TABLE_RADIUS + 0.6, TABLE_HEIGHT, 72);
    const table = new THREE.Mesh(tableGeometry, [sideMaterial, topMaterial, sideMaterial]);
    table.position.y = TABLE_TOP_Y - TABLE_HEIGHT / 2;
    table.receiveShadow = true;
    this.scene.add(table);

    // Rebord releve en bordure, pour un vrai aspect "plateau de table" plutot qu'un disque plat.
    const rim = new THREE.Mesh(new THREE.TorusGeometry(TABLE_RADIUS - 0.12, 0.14, 14, 80), sideMaterial);
    rim.rotation.x = Math.PI / 2;
    rim.position.y = TABLE_TOP_Y - 0.02;
    rim.receiveShadow = true;
    rim.castShadow = true;
    this.scene.add(rim);
  }

  /**
   * Champ de vision (vertical, tel que le definit three.js) necessaire pour que le
   * plateau (cadre compris, plus la marge `BOARD_FIT_MARGIN`) tienne entierement
   * dans la largeur de l'ecran, camera a distance fixe (`RADIUS`). A cette distance,
   * la demi-largeur monde visible vaut `RADIUS * tan(fov/2) * aspect` (identite
   * `tan(atan(x)) = x` appliquee a la formule standard du FOV horizontal three.js) :
   * on en deduit le FOV minimal requis. En format large (bureau), ce FOV requis est
   * plus petit que `BASE_FOV` : on garde alors `BASE_FOV` (cadrage bureau inchange).
   * En format portrait (mobile), il le depasse : elargir le FOV (plutot que reculer
   * la camera) evite de sortir de la zone de brouillard de la scene (`scene.fog`,
   * qui commence a 14 unites — la camera reste fixe a 13.5).
   */
  private computeFov(): number {
    const aspect = this.camera.aspect || 1;
    const targetHalfWidth = BOARD_OUTER_HALF_SPAN * BOARD_FIT_MARGIN;
    const requiredHalfFov = Math.atan(targetHalfWidth / (RADIUS * aspect));
    const requiredFov = THREE.MathUtils.radToDeg(requiredHalfFov) * 2;
    return THREE.MathUtils.clamp(Math.max(BASE_FOV, requiredFov), BASE_FOV, MAX_FOV);
  }

  private updateCameraPosition(): void {
    const azimuth = this.baseAzimuth + this.userAzimuthOffset;
    const elevation = ELEVATION_FROM_HORIZONTAL + this.userElevationOffset + this.introElevationBoost;
    const radius = RADIUS * this.introRadiusMultiplier;
    const horizontalDist = radius * Math.cos(elevation);
    const x = horizontalDist * Math.sin(azimuth);
    const y = radius * Math.sin(elevation);
    const z = -horizontalDist * Math.cos(azimuth);
    this.camera.position.set(x, y, z);
    this.camera.lookAt(0, 0, 0);
  }

  /**
   * Entree en scene "plongeante" : place la camera tres en hauteur et en retrait
   * (quasi a la verticale du plateau), puis la fait plonger vers sa position de jeu
   * normale. A appeler juste apres la construction de la scene (camera encore
   * masquee par le rideau noir), en parallele du fondu (`SceneFade.fromBlack`) —
   * les deux effets se chevauchent volontairement pour l'entree en jeu.
   */
  async playIntroSwoop(duration = INTRO_SWOOP_DURATION): Promise<void> {
    const startElevationBoost = INTRO_START_ELEVATION - ELEVATION_FROM_HORIZONTAL;
    const startRadiusExtra = INTRO_START_RADIUS_SCALE - 1;
    this.introElevationBoost = startElevationBoost;
    this.introRadiusMultiplier = INTRO_START_RADIUS_SCALE;
    this.updateCameraPosition();
    await this.tweens.animate(duration, (t) => {
      this.introElevationBoost = startElevationBoost * (1 - t);
      this.introRadiusMultiplier = 1 + startRadiusExtra * (1 - t);
      this.updateCameraPosition();
    });
    this.introElevationBoost = 0;
    this.introRadiusMultiplier = 1;
    this.updateCameraPosition();
  }

  /**
   * Oriente instantanement la camera (sans animation) vers le cote du joueur
   * `color`, sans repasser par la marge de rotation libre en cours. Utilise
   * en mode "vs IA" (un seul humain devant l'ecran) pour fixer une bonne
   * fois pour toutes son point de vue, sans le pivot automatique de tour.
   */
  setBaseAzimuth(color: Color): void {
    this.baseAzimuth = color === "w" ? 0 : Math.PI;
    this.userAzimuthOffset = 0;
    this.userElevationOffset = 0;
    this.updateCameraPosition();
  }

  /** Fait pivoter la camera de 180 degres en azimut vers le cote du joueur `color`. */
  async pivotToPlayer(color: Color): Promise<void> {
    const start = this.baseAzimuth;
    const end = start + Math.PI;
    const startUserAzimuth = this.userAzimuthOffset;
    const startUserElevation = this.userElevationOffset;
    void color;
    await this.tweens.animate(PIVOT_DURATION, (t) => {
      this.baseAzimuth = start + (end - start) * t;
      this.userAzimuthOffset = startUserAzimuth * (1 - t);
      this.userElevationOffset = startUserElevation * (1 - t);
      this.updateCameraPosition();
    });
  }

  private onPointerDown = (event: PointerEvent): void => {
    this.dragging = true;
    this.lastPointer = { x: event.clientX, y: event.clientY };
  };

  private onPointerMove = (event: PointerEvent): void => {
    if (!this.dragging) return;
    const dx = event.clientX - this.lastPointer.x;
    const dy = event.clientY - this.lastPointer.y;
    this.lastPointer = { x: event.clientX, y: event.clientY };
    this.userAzimuthOffset = THREE.MathUtils.clamp(
      this.userAzimuthOffset + dx * 0.005,
      -USER_AZIMUTH_MARGIN,
      USER_AZIMUTH_MARGIN,
    );
    this.userElevationOffset = THREE.MathUtils.clamp(
      this.userElevationOffset + dy * 0.004,
      -USER_ELEVATION_MARGIN,
      USER_ELEVATION_MARGIN,
    );
    this.updateCameraPosition();
  };

  private onPointerUp = (): void => {
    this.dragging = false;
  };

  raycastFromPointer(clientX: number, clientY: number, objects: THREE.Object3D[]): THREE.Intersection[] {
    const rect = this.renderer.domElement.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(ndc, this.camera);
    return this.raycaster.intersectObjects(objects, true);
  }

  /**
   * Change de niveau de rendu a chaud, y compris en cours de partie (pas seulement
   * au demarrage) : resolution, ombres (activation + resolution) et plafond de
   * cadence sont tous modifiables sans reconstruire la scene. Seul l'antialiasing
   * reste fixe (fige a la creation du contexte WebGL par three.js), d'ou son
   * absence de `QualityConfig`.
   */
  setQuality(quality: GraphicsQuality): void {
    this.quality = QUALITY_CONFIGS[quality];

    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, this.quality.pixelRatioCap));
    this.handleResize();

    // La render target d'ombre existante reste a l'ancienne resolution tant qu'on
    // ne la force pas a se reconstruire : on la jette, three.js en recree une (a la
    // bonne taille) au prochain rendu si des ombres sont a nouveau demandees.
    this.keyLight.shadow.map?.dispose();
    this.keyLight.shadow.map = null;
    this.keyLight.castShadow = this.quality.shadowsEnabled;
    this.renderer.shadowMap.enabled = this.quality.shadowsEnabled;
    if (this.quality.shadowsEnabled) {
      this.keyLight.shadow.mapSize.set(this.quality.shadowMapSize, this.quality.shadowMapSize);
      this.renderer.shadowMap.needsUpdate = true;
    }

    this.frameAccumulator = 0;

    if (!this.quality.animateCandles) {
      // Fige les chandelles a une intensite/taille de repos plutot que de les
      // laisser au milieu d'un vacillement (valeur arbitraire figee sinon).
      this.candles.forEach((candle) => {
        candle.light.intensity = CANDLE_REST_INTENSITY;
        candle.flame.scale.copy(CANDLE_REST_SCALE);
      });
    }
  }

  private handleResize = (): void => {
    const { clientWidth, clientHeight } = this.container;
    this.camera.aspect = clientWidth / Math.max(1, clientHeight);
    this.camera.fov = this.computeFov();
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(clientWidth, clientHeight);
  };

  private loop = (): void => {
    if (this.disposed) return;
    requestAnimationFrame(this.loop);
    const dt = Math.min(this.clock.getDelta(), MAX_FRAME_DELTA);

    // Mode "battery" : cadence plafonnee (30 im/s) plutot que la cadence native de
    // l'ecran (souvent 60-120 im/s sur mobile) — la moitie (voire le quart) moins
    // d'images a calculer, pour un jeu de plateau ou la fluidite maximale n'apporte
    // rien. Le temps ecoule s'accumule meme sur les frames sautees, pour que les
    // tweens restent a la bonne vitesse (pas de ralenti) une fois la frame rendue.
    let elapsed = dt;
    if (this.quality.targetFps !== null) {
      this.frameAccumulator += dt;
      const targetInterval = 1 / this.quality.targetFps;
      if (this.frameAccumulator < targetInterval) return;
      elapsed = this.frameAccumulator;
      this.frameAccumulator = 0;
    }

    const tweenActive = this.tweens.update(elapsed);

    if (this.quality.animateCandles) {
      const flickerTime = this.clock.elapsedTime;
      this.candles.forEach((candle, i) => {
        const flicker = Math.sin(flickerTime * 7 + i * 3.1) * 0.5 + Math.sin(flickerTime * 17 + i) * 0.3;
        candle.light.intensity = 4.6 + flicker;
        candle.flame.scale.set(0.65 + flicker * 0.05, 1.4 + flicker * 0.15, 0.65 + flicker * 0.05);
      });
    }

    if (this.quality.shadowsEnabled && tweenActive) {
      this.renderer.shadowMap.needsUpdate = true;
    }

    this.renderer.render(this.scene, this.camera);
  };

  dispose(): void {
    this.disposed = true;
    window.removeEventListener("resize", this.handleResize);
    window.removeEventListener("pointermove", this.onPointerMove);
    window.removeEventListener("pointerup", this.onPointerUp);
  }
}
